import React, { useEffect, useMemo, useState } from 'react';
import { useIntl } from 'umi';
import {
  Button,
  Checkbox,
  Form,
  Modal,
  Radio,
  Switch,
} from 'tea-component';
import InputNumber from '@/pages/intelligence/components/Form/components/common/InputNumber';
import { MAX_ROWS_LIMIT } from '@intelligence/components/DownloadCommon/DownloadPage/utils';
import {
  getDefaultRobloxTopChartsDownloadGameInfo,
  ROBLOX_TOP_CHARTS_DOWNLOAD_GAME_INFO_OPTIONS,
} from '../../config/robloxTopChartsDownloadConfig';
import { buildRobloxTopChartsDownloadParams } from '../../utils/buildRobloxTopChartsDownloadParams';
import { reportRobloxTopChartsDownloadLog } from '../../config/pageLogConfig';

import type { TableSelectionType } from '@intelligence/components/TableComponents/components/TableCheckbox';
import type { IMainGenre } from '@intelligence/types/common';
import type { IRobloxTopChartsFilterValues } from '../../types';
import type { IRobloxTopChartsDownloadFormValues } from './types';

import './index.scss';
import '@intelligence/components/ExportTable/components/DownloadSettingModal/index.scss';

interface IRobloxTopChartsDownloadProps {
  filterValues: IRobloxTopChartsFilterValues;
  tableSelection: TableSelectionType[];
  regionOptions?: IRecord[];
  total?: number;
  robloxGenreOptions?: IMainGenre[];
  sortKey?: string;
  sortOrder?: 'asc' | 'desc';
}

const getDefaultFormValues = (): IRobloxTopChartsDownloadFormValues => ({
  gameInfo: getDefaultRobloxTopChartsDownloadGameInfo(),
  date_method: 'aggregated',
  region_method: 'aggregated',
  ratio_show: true,
  data_method: 'selectTop',
  data_amount: 100,
  file_format: 'xlsx',
  game_ids: [],
});

const RobloxTopChartsDownload: React.FC<IRobloxTopChartsDownloadProps> = ({
  filterValues,
  tableSelection,
  regionOptions = [],
  total = MAX_ROWS_LIMIT,
  robloxGenreOptions = [],
  sortKey,
  sortOrder,
}) => {
  const { formatMessage } = useIntl();
  const [visible, setVisible] = useState(false);
  const [isBlur, setIsBlur] = useState(false);
  const [formValues, setFormValues] = useState<IRobloxTopChartsDownloadFormValues>(
    getDefaultFormValues,
  );

  const selectedGameIds = useMemo(() => {
    return tableSelection
      .map((item) => String(item?.gameId || ''))
      .filter(Boolean);
  }, [tableSelection]);

  useEffect(() => {
    if (!visible) {
      return;
    }

    setFormValues({
      ...getDefaultFormValues(),
      game_ids: selectedGameIds,
    });
    setIsBlur(false);
  }, [visible, selectedGameIds]);

  const handleConfirm = async (): Promise<void> => {
    if (formValues.data_method === 'selectTop' && formValues.data_amount < 1) {
      setIsBlur(true);
      return;
    }

    const downLoadParams = buildRobloxTopChartsDownloadParams({
      filterValues,
      formValues: {
        ...formValues,
        game_ids: formValues.data_method === 'checkedLines' ? selectedGameIds : [],
      },
      regionOptions,
      robloxGenreOptions,
      sortKey,
      sortOrder,
    });

    const { downloadAction } = await import(
      '@intelligence/components/DownloadCommon/DownloadPage'
    );
    downloadAction({
      downloadKey: 'robloxTopCharts',
      downLoadParams,
    });
    reportRobloxTopChartsDownloadLog();
    setVisible(false);
  };

  const selectedGameInfoKeys = useMemo(() => {
    return ROBLOX_TOP_CHARTS_DOWNLOAD_GAME_INFO_OPTIONS
      .filter((item) => formValues.gameInfo[item.key])
      .map((item) => item.key);
  }, [formValues.gameInfo]);

  return (
    <>
      <Button
        icon="download"
        className="ns-btn--icon trend-justify-btn dbi-roblox-top-charts-download-btn"
        onClick={() => setVisible(true)}
      />

      <Modal
        visible={visible}
        size={860}
        caption={formatMessage({ id: 'dbi_download_settings' })}
        className="dbi-roblox-top-charts-download-modal dbi-download-type"
        onClose={() => setVisible(false)}
      >
        <Modal.Body>
          <Form className="dbi-download-form teaui-form-alignment dbi-roblox-top-charts-download-form pc-console-topCharts">
            <Form.Item
              label={formatMessage({ id: 'dbi_roblox_download_game_info' })}
              showStatusIcon={false}
            >
              <div className="dbi-roblox-top-charts-download-form__game-info">
                <Checkbox.Group
                  value={selectedGameInfoKeys}
                  onChange={(checkedValues) => {
                    const nextGameInfo = getDefaultRobloxTopChartsDownloadGameInfo();

                    checkedValues.forEach((value) => {
                      if (value in nextGameInfo) {
                        nextGameInfo[value as keyof typeof nextGameInfo] = true;
                      }
                    });

                    setFormValues((prev) => ({
                      ...prev,
                      gameInfo: nextGameInfo,
                    }));
                  }}
                >
                  {ROBLOX_TOP_CHARTS_DOWNLOAD_GAME_INFO_OPTIONS.map((item) => (
                    <Checkbox key={item.key} name={item.key}>
                      {formatMessage({ id: item.labelKey })}
                    </Checkbox>
                  ))}
                </Checkbox.Group>
              </div>
            </Form.Item>

            <Form.Item
              label={formatMessage({ id: 'dbi_label_data_calculation' })}
              showStatusIcon={false}
            >
              <div className="dbi-roblox-top-charts-download-form__calculation">
                <Form.Item
                  label={formatMessage({ id: 'dbi_label_time_settings' })}
                  showStatusIcon={false}
                >
                  <Radio.Group
                    value={formValues.date_method}
                    onChange={(value) => {
                      setFormValues((prev) => ({
                        ...prev,
                        date_method: value as IRobloxTopChartsDownloadFormValues['date_method'],
                      }));
                    }}
                  >
                    <Radio name="aggregated">
                      {formatMessage({ id: 'dbi_label_aggregated' })}
                    </Radio>
                    <Radio name="separate">
                      {formatMessage({ id: 'dbi_label_separate_by_time_combine' })}
                    </Radio>
                  </Radio.Group>
                </Form.Item>

                <Form.Item
                  label={formatMessage({ id: 'dbi_label_region_settings' })}
                  showStatusIcon={false}
                >
                  <Radio.Group
                    value={formValues.region_method}
                    onChange={(value) => {
                      setFormValues((prev) => ({
                        ...prev,
                        region_method: value as IRobloxTopChartsDownloadFormValues['region_method'],
                      }));
                    }}
                  >
                    <Radio name="aggregated">
                      {formatMessage({ id: 'dbi_label_aggregated' })}
                    </Radio>
                    <Radio name="market">
                      {formatMessage({ id: 'dbi_label_separate_by_country_combine' })}
                    </Radio>
                    <Radio name="region">
                      {formatMessage({ id: 'dbi_label_separate_by_region_combine' })}
                    </Radio>
                  </Radio.Group>
                </Form.Item>

                <Form.Item
                  label={formatMessage({ id: 'dbi_label_ratio' })}
                  showStatusIcon={false}
                >
                  <Switch
                    value={formValues.ratio_show}
                    onChange={(value) => {
                      setFormValues((prev) => ({
                        ...prev,
                        ratio_show: value,
                      }));
                    }}
                  />
                </Form.Item>
              </div>
            </Form.Item>

            <Form.Item
              label={formatMessage({ id: 'dbi_label_data_amount' })}
              showStatusIcon={false}
            >
              <Radio.Group
                value={formValues.data_method}
                onChange={(value) => {
                  setFormValues((prev) => ({
                    ...prev,
                    data_method: value as IRobloxTopChartsDownloadFormValues['data_method'],
                  }));
                }}
              >
                <Radio name="selectAll">
                  {formatMessage({ id: 'dbi_export_all_data' })}
                </Radio>
                <Radio name="selectTop">
                  {formatMessage({ id: 'dbi_select_top' })}
                </Radio>
                <Radio
                  name="checkedLines"
                  disabled={!selectedGameIds.length}
                >
                  {formatMessage({ id: 'dbi_export_checked_lines' })}
                </Radio>
              </Radio.Group>
            </Form.Item>

            {formValues.data_method === 'selectTop' ? (
              <Form.Item
                label=""
                showStatusIcon={false}
                status={isBlur ? 'error' : 'validating'}
              >
                <InputNumber
                  field="data_amount"
                  value={formValues.data_amount}
                  total={total}
                  isAutoAdjustMaxTipWidth
                  onChange={(_field, value) => {
                    const valueNum = Number.isNaN(value) ? 0 : value;
                    if (valueNum < 1) {
                      setFormValues((prev) => ({ ...prev, data_amount: 0 }));
                      setIsBlur(true);
                      return;
                    }

                    setFormValues((prev) => ({ ...prev, data_amount: valueNum }));
                    setIsBlur(false);
                  }}
                />
              </Form.Item>
            ) : null}

            <Form.Item
              label={formatMessage({ id: 'dbi_label_file_format' })}
              showStatusIcon={false}
            >
              <Radio.Group
                value={formValues.file_format}
                onChange={(value) => {
                  setFormValues((prev) => ({
                    ...prev,
                    file_format: value as IRobloxTopChartsDownloadFormValues['file_format'],
                  }));
                }}
              >
                <Radio name="xlsx">Excel</Radio>
                <Radio name="csv">CSV</Radio>
              </Radio.Group>
            </Form.Item>

            <Form.Item label="   ">
              <Form.Text style={{ color: '#9f9f9f' }}>
                {formatMessage({ id: 'dbi_download_modal_stick_tips' })}
              </Form.Text>
            </Form.Item>
          </Form>
        </Modal.Body>

        <Modal.Footer>
          <Button type="primary" onClick={handleConfirm}>
            {formatMessage({ id: 'dbi_confirm' })}
          </Button>
          <Button onClick={() => setVisible(false)}>
            {formatMessage({ id: 'dbi_cancel' })}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default RobloxTopChartsDownload;
